<template>
    <div class="demo-container" ref="demoContainer">
        <!-- Question Header -->
        <div class="demo-header">
            <span class="demo-badge difficulty">{{ activeQuestion.difficulty }}</span>
            <span class="demo-badge type">{{ activeQuestion.type }}</span>
            <span class="demo-badge marks">{{ activeQuestion.marks }} marks</span>
        </div>

        <!-- Question -->
        <div class="demo-question">
            <h4>Question:</h4>
            <p v-if="activeQuestion.sharedContext" class="shared-context">
                {{ activeQuestion.sharedContext }}
            </p>
            <p class="question-text">{{ activeQuestion.question }}</p>
            <div v-if="activeQuestion.imageUrl" class="demo-image-placeholder">
                {{ activeQuestion.imageUrl }}
            </div>
        </div>

        <!-- Answer Input (Auto-filling) -->
        <div v-if="!showFeedback" class="demo-answer-section">
            <label class="answer-label">Your Answer:</label>
            <div class="demo-input-area">
                <textarea
                    :value="typedAnswer"
                    placeholder="Type your answer here..."
                    rows="6"
                    readonly
                ></textarea>
            </div>

            <!-- Math Editor -->
            <div class="demo-math-container">
                <div class="demo-mathfield">
                    <span v-if="!typedMath" class="math-placeholder"
                        >Type your equations here...</span
                    >
                    <span v-else class="math-content">{{ typedMath }}</span>
                </div>
                <button
                    class="demo-tool-btn canvas-toggle"
                    :class="{ active: canvasOpen }"
                >
                    CANVAS
                </button>
                <button class="demo-tool-btn insert-btn">↵</button>
            </div>

            <!-- Canvas Demo (appears briefly) -->
            <div v-if="canvasOpen" class="demo-canvas">
                <div class="canvas-header">
                    <span>Drawing Canvas</span>
                    <button class="close-btn">×</button>
                </div>
                <div class="canvas-body">
                    <div class="canvas-controls-left">
                        <button class="canvas-control-btn active">
                            <span class="control-icon">✏️</span>
                            <span class="control-label">Draw</span>
                        </button>
                        <button class="canvas-control-btn destructive">
                            <span class="control-icon">🗑️</span>
                            <span class="control-label">Clear</span>
                        </button>
                        <button class="canvas-control-btn">
                            <span class="control-icon">💾</span>
                            <span class="control-label">Save</span>
                        </button>
                        <button class="canvas-control-btn">
                            <span class="control-icon">📏</span>
                            <span class="control-label">Lines</span>
                        </button>
                    </div>
                    <div class="canvas-area">
                        <div class="canvas-placeholder">
                            <p>🎨 Interactive drawing canvas</p>
                            <p
                                style="
                                    font-size: 13px;
                                    opacity: 0.7;
                                    margin-top: 8px;
                                "
                            >
                                Draw molecular structures, mechanisms, and
                                diagrams
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Canvas Preview -->
            <div v-if="showThumbnail" class="demo-thumbnails">
                <div class="thumbnail">
                    <div class="thumbnail-content">
                        🧪 Molecular structure drawing
                    </div>
                    <button class="thumbnail-delete">×</button>
                </div>
            </div>

            <button class="demo-submit-btn" :disabled="!submitting">
                {{ submitting ? "Submitting..." : "Submit Answer" }}
            </button>
        </div>

        <!-- Feedback -->
        <div v-else class="demo-feedback">
            <h4 class="answer-label">Your Answer:</h4>
            <div class="demo-input-area locked-answer">
                <textarea
                    :value="typedAnswer"
                    placeholder="Type your answer here..."
                    rows="6"
                    readonly
                ></textarea>
            </div>
            <div
                class="feedback-header"
                :class="
                    activeQuestion.awardedMarks === activeQuestion.marks
                        ? 'correct'
                        : 'incorrect'
                "
            >
                <span class="feedback-icon">
                    {{
                        activeQuestion.awardedMarks === activeQuestion.marks
                            ? "✓"
                            : "✗"
                    }}
                </span>

                <span class="feedback-title">
                    {{
                        activeQuestion.awardedMarks === activeQuestion.marks
                            ? "Correct!"
                            : "Partial Credit"
                    }}
                </span>

                <span class="feedback-marks" :class="activeQuestion.awardedMarks === activeQuestion.marks ? 'correct' : 'incorrect'">
                    {{ activeQuestion.awardedMarks }} /
                    {{ activeQuestion.marks }} marks
                </span>
            </div>

            <div class="feedback-content">
                <div class="specific-critique">
                    <h4>Marker Feedback:</h4>
                    <p>
                        <vue-writer
                            :typeSpeed="10"
                            :iterations="1"
                            :array="[activeQuestion.feedback]"
                        ></vue-writer>
                    </p>
                </div>

                <hr style="margin: 10px 0px" />

                <h4>Explanation:</h4>
                <p>{{ activeQuestion.explanation }}</p>

                <div class="feedback-model-answer">
                    <h4>Model Answer:</h4>
                    <p style="white-space: pre-wrap">
                        {{ activeQuestion.modelAnswer }}
                    </p>
                </div>
            </div>

            <button @click="runDemoSequence" class="demo-next-btn">
                Try Another Question
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, onBeforeUnmount } from "vue";
import { VueWriter } from "vue-writer";

const props = defineProps({
    question: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(["next"]);

const isVisible = ref(false);
const hasStarted = ref(false);
const hasEverStarted = ref(false);
const observer = ref(null);
const demoContainer = ref(null);

const typedAnswer = ref("");
const typedMath = ref("");
const canvasOpen = ref(false);
const showThumbnail = ref(false);
const submitting = ref(false);
const showFeedback = ref(false);
const demoIndex = ref(0);

const activeQuestion = computed(() => {
    return demoQuestions[demoIndex.value];
});

const demoQuestions = [
    {
        difficulty: "Band 5",
        type: "Extended Response",
        marks: 4,
        question:
            "A buffer solution contains 0.50 M ethanoic acid (CH₃COOH) and 0.50 M sodium ethanoate (CH₃COONa). The Ka for ethanoic acid is 1.8 × 10⁻⁵.\n\n(a) Calculate the pH of this buffer solution. (2 marks)\n(b) Explain how this buffer resists changes in pH when a small amount of acid is added. (2 marks)",
        sharedContext: null,
        imageUrl: null,
        correctAnswer: "pH ≈ 4.74",
        isCorrect: false,
        demoAnswer:
            "(a) pH = pKa + log([base]/[acid])\npKa = -log(1.8 × 10⁻⁵) = 4.74\npH = 4.74 + log(0.50/0.50) = 4.74\n\n(b) When acid is added, the H⁺ ions react with the sodium ethanoate. This shifts the equilibrium to the left to make more ethanoic acid. This consumes the extra acid so the pH doesn't change much.",
        demoMath:
            "pK_a = -\\log(1.8 \times 10^{-5}) = 4.74 \\ pH = 4.74 + \\log\\left(\frac{0.50}{0.50}\right) = 4.74",
        feedback:
            "Part (a) — 2/2 marks: The calculation is perfect. The student showed the formula, the pKa conversion, and the final result.\nPart (b) — 1/2 marks: The student correctly identifies that the added acid is consumed and mentions the equilibrium shift. However, they lose a mark for lack of specificity. To get full marks, they should have explicitly named the ethanoate ion (CH₃COO⁻) as the reacting species or provided the ionic equation: CH₃COO⁻ + H⁺ → CH₃COOH",
        awardedMarks: 3,
        explanation:
            "This buffer uses the Henderson-Hasselbalch equation. Since [acid] = [conjugate base], pH = pKa. The buffer resists pH changes through Le Chatelier's principle - added H⁺ ions are consumed by CH₃COO⁻ to form more CH₃COOH, minimising pH change.",
        modelAnswer:
            "(a) Using Henderson-Hasselbalch: pH = pKa + log([A⁻]/[HA])\npKa = -log(1.8×10⁻⁵) = 4.74\npH = 4.74 + log(0.50/0.50) = 4.74 + 0 = 4.74\n\n(b) When acid (H⁺) is added, the ethanoate ions (CH₃COO⁻) react with the H⁺ to form ethanoic acid (CH₃COOH), removing most of the added H⁺. This maintains the ratio [A⁻]/[HA] close to 1, so pH changes minimally.",
    },
    {
        difficulty: "Band 4",
        type: "Extended Response",
        marks: 3,
        question:
            "Explain why ethanoic acid (CH₃COOH) is a weak acid while hydrochloric acid (HCl) is a strong acid. In your answer, refer to the degree of ionisation in aqueous solution, the strength of the bonds being broken and the equilibrium position (1 mark)",
        sharedContext: null,
        imageUrl: null,
        correctAnswer:
            "Weak acid partially ionises, strong acid fully dissociates",
        isCorrect: false,
        demoAnswer:
            "Hydrochloric acid is a strong acid because it completely ionises in water, whereas ethanoic acid only partially ionises. This happens because the H-Cl bond is relatively weak and easy to break, but the O-H bond in ethanoic acid is much stronger. In terms of equilibrium, HCl reacts fully to form products, but ethanoic acid exists in an equilibrium that stays mostly in the middle.",
        feedback:
            "You have provided a strong comparison of the degree of ionisation and correctly identified that bond strength dictates how easily the $H^+$ ion is released. However, you lost one mark in your discussion of equilibrium. While you correctly noted that ethanoic acid reaches an equilibrium state, you described it as staying 'in the middle.' In a weak acid like ethanoic acid, the equilibrium lies significantly to the left (favoring the un-ionised molecules). You should also specify that for a strong acid like HCl, the equilibrium position lies effectively to the right.",
        awardedMarks: 2,
        explanation:
            "Acid strength depends on how readily the H⁺ ion is released in water. HCl completely dissociates because the H-Cl bond is weak and polar. Ethanoic acid only partially ionises because the O-H bond is stronger and the equilibrium favors the molecular form.",
        modelAnswer:
            "HCl ionises completely (100%) in water, producing H⁺ and Cl⁻ ions. Ethanoic acid only partially ionises (~1%), with most molecules remaining as CH₃COOH.\nThe H-Cl bond is highly polar and relatively weak, easily breaking in water. The O-H bond in ethanoic acid is stronger due to the electron-withdrawing effect of the carbonyl group being partially offset by the methyl group.\nFor HCl, the equilibrium lies completely to the right (products). For ethanoic acid, the equilibrium lies to the left (reactants), represented by: CH₃COOH(aq) ⇌ CH₃COO⁻(aq) + H⁺(aq)",
    },
];

const runDemoSequence = async () => {
    demoIndex.value = (demoIndex.value + 1) % demoQuestions.length;
    hasStarted.value = false;
    hasEverStarted.value = true;

    typedAnswer.value = "";
    typedMath.value = "";
    canvasOpen.value = false;
    showThumbnail.value = false;
    submitting.value = false;
    showFeedback.value = false;

    // Wait 1s before starting
    const currentQ = demoQuestions[demoIndex.value];
    await new Promise((r) => setTimeout(r, 1000));

    // Type answer
    await typeText(currentQ.demoAnswer, typedAnswer);
    await new Promise((r) => setTimeout(r, 800));

    // Show math field with equation
    if (currentQ.demoMath) {
        await typeText(currentQ.demoMath, typedMath);
        await new Promise((r) => setTimeout(r, 600));
    }

    // Open canvas briefly
    canvasOpen.value = true;
    await new Promise((r) => setTimeout(r, 1500));

    // Close canvas and show thumbnail
    canvasOpen.value = false;
    showThumbnail.value = true;
    await new Promise((r) => setTimeout(r, 800));

    // Submit
    submitting.value = true;
    await new Promise((r) => setTimeout(r, 1200));

    // Show feedback
    showFeedback.value = true;
};

const typeText = async (text, target) => {
    for (let i = 0; i <= text.length; i++) {
        target.value = text.substring(0, i);
        await new Promise((r) => setTimeout(r, 10));
    }
};

onMounted(() => {
    // Create intersection observer
    observer.value = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !hasStarted.value && !hasEverStarted.value) {
                    isVisible.value = true;
                    hasStarted.value = true;
                    runDemoSequence();
                }
            });
        },
        {
            threshold: 0.3, // Start when 30% visible
            rootMargin: '0px'
        }
    );

    // Start observing
    if (demoContainer.value) {
        observer.value.observe(demoContainer.value);
    }
});

onBeforeUnmount(() => {
    if (observer.value && demoContainer.value) {
        observer.value.unobserve(demoContainer.value);
    }
});
</script>

<style scoped>
.demo-container {
    background: rgba(var(--bg-secondary-rgb), 0.95);
    border: 1px solid var(--accent);
    padding: 30px;
    position: relative;
    z-index: 5;
}

.demo-header {
    display: flex;
    gap: 10px;
    margin-bottom: 25px;
    flex-wrap: wrap;
}

.demo-badge {
    padding: 6px 14px;
    font-size: 13px;
    font-weight: 600;
}

.demo-badge.difficulty {
    background: var(--bg-secondary);
    color: var(--text-secondary);
    border: 1px solid rgba(var(--accent-rgb), 0.5)
}

.demo-badge.type {
    background: rgba(255, 246, 227, 0.1);
    color: var(--text-primary);
    border: 1px solid var(--accent);
}

.demo-badge.marks {
    background: var(--accent);
    color: var(--bg-primary);
}

.demo-question {
    text-align: left;
    margin-bottom: 30px;
}

.demo-question h4 {
    color: var(--text-secondary);
    font-size: 16px;
    margin-bottom: 12px;
}

.demo-question .shared-context {
    color: var(--text-primary);
    font-size: 15px;
    line-height: 1.6;
    margin-bottom: 12px;
    opacity: 0.8;
    font-style: italic;
}

.demo-question .question-text {
    color: var(--text-primary);
    font-size: 16px;
    line-height: 1.7;
    margin-bottom: 15px;
    white-space: pre-wrap;
}

.demo-image-placeholder {
    background: var(--bg-secondary);
    border: 1px dashed var(--accent);
    padding: 40px;
    text-align: center;
    color: var(--text-secondary);
    font-style: italic;
}

.demo-answer-section {
    margin-bottom: 20px;
}

.demo-answer-section label {
    display: block;
    margin-bottom: 10px;
    font-weight: 600;
    color: var(--text-secondary);
    font-size: 15px;
}

.demo-input-area textarea {
    width: 100%;
    padding: 12px;
    background: rgba(var(--bg-primary-rgb), 0.4);
    border: 2px solid var(--accent);
    color: var(--text-primary);
    font-size: 15px;
    resize: vertical;
    font-family: "Space Grotesk", sans-serif;
    line-height: 1.5;
    box-sizing: border-box;
}

.demo-input-area textarea:focus {
    outline: none;
    border-color: var(--accent);
}

.demo-math-container {
    display: flex;
    gap: 6px;
    margin: 10px 0 20px 0;
}

.demo-mathfield {
    flex: 1;
    padding: 12px;
    background: rgba(var(--bg-primary-rgb), 0.4);
    border: 2px solid var(--accent);
    color: var(--text-primary);
    font-size: 16px;
    min-height: 46px;
    cursor: text;
    display: flex;
    align-items: center;
}

.math-placeholder {
    color: var(--text-primary);
    opacity: 0.5;
    font-style: italic;
}

.math-content {
    color: var(--text-primary);
    font-family: "KaTeX_Main", "Times New Roman", serif;
}

.demo-tool-btn {
    padding: 12px 18px;
    border: none;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.2s;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
}

.demo-tool-btn.canvas-toggle {
    background: var(--bg-secondary);
    color: var(--text-secondary);
    border: 2px solid var(--accent);
}

.demo-tool-btn.canvas-toggle.active {
    background: var(--bg-secondary);
    border-color: var(--accent);
}

.demo-tool-btn.insert-btn {
    background: var(--accent);
    color: var(--bg-primary);
    padding-left: 22px;
    padding-right: 22px;
}

.canvas-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    background: var(--bg-secondary);
    color: var(--text-secondary);
    font-weight: 600;
}

.close-btn {
    background: none;
    border: none;
    color: var(--text-primary);
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.canvas-body {
    display: flex;
    gap: 6px;
    padding: 0;
}

.canvas-controls-left {
    display: flex;
    flex-direction: column;
    width: 60px;
    flex-shrink: 0;
}

.canvas-control-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 14px 8px;
    background: rgba(19, 19, 19, 0.6);
    border: 2px solid var(--accent);
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s;
    flex: 1;
}

.canvas-control-btn.active {
    background: rgba(254, 231, 179, 0.15);
    border-color: var(--accent);
}

.canvas-control-btn.destructive:hover:not(:disabled) {
    border-color: #ff6b6b;
    background: rgba(255, 107, 107, 0.1);
}

.control-icon {
    font-size: 20px;
    line-height: 1;
}

.control-label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.canvas-area {
    flex: 1;
    position: relative;
    background: #000;
    border: 2px solid var(--accent);
    min-height: 400px;
}

.canvas-placeholder {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    color: var(--text-secondary);
    pointer-events: none;
}

.demo-thumbnails {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin: 15px 0;
}

.thumbnail {
    position: relative;
    background: var(--bg-secondary);
    border: 1px solid var(--accent);
    padding: 15px;
    color: var(--text-primary);
    font-size: 14px;
}

.thumbnail-delete {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 24px;
    height: 24px;
    background: var(--accent);
    color: var(--bg-primary);
    border: none;
    border-radius: 50%;
    cursor: pointer;
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.demo-submit-btn {
    width: 100%;
    padding: 16px;
    background: var(--accent);
    color: var(--bg-primary);
    border: none;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.demo-submit-btn:disabled {
    opacity: 0.7;
}

.demo-feedback {
    text-align: left;
}

.feedback-header {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 20px;
    margin-bottom: 25px;
    border: 2px solid;
}

.feedback-header.correct {
    background: rgba(76, 175, 80, 0.1);
    border-color: #4caf50;
}

.feedback-header.incorrect {
    background: rgba(244, 67, 54, 0.1);
    border-color: #f44336;
}

.feedback-icon {
    font-size: 28px;
    line-height: 1;
    color: var(--text-primary);
}

.feedback-title {
    flex: 1;
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
}

.feedback-marks {
    font-size: 18px;
    font-weight: 700;
}

.feedback-marks.correct {
    color: #4caf50;
}

.feedback-marks.incorrect {
    color: #f44336;
}

.feedback-content {
    margin-bottom: 25px;
}

.feedback-content h4 {
    color: var(--text-secondary);
    margin-bottom: 10px;
    font-size: 16px;
    font-weight: 600;
}

.feedback-content p {
    color: var(--text-primary);
    line-height: 1.6;
    opacity: 0.9;
}

.feedback-model-answer {
    background: rgba(255, 255, 255, 0.05);
    border-left: 3px solid var(--text-secondary);
    padding: 20px;
    margin-top: 20px;
}

.feedback-model-answer h4 {
    color: var(--text-secondary);
    font-size: 16px;
}

.feedback-model-answer p {
    font-family: "Space Grotesk", monospace;
    white-space: pre-wrap;
    font-size: 14px;
    line-height: 1.7;
}

.demo-next-btn {
    width: 100%;
    padding: 16px;
    background: var(--accent);
    color: var(--bg-primary);
    border: none;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.demo-next-btn:hover {
    transform: translateY(-2px);
}

.locked-answer {
    margin: 20px 0px;
}

.answer-label {
    display: block;
    font-weight: 600;
    color: var(--text-secondary);
    font-size: 15px;
}
</style>
